/**
 * The comparables on a map, with the subject.
 *
 * A comp table is a list of places; whether they are comparable is partly a
 * question of where they stand. Every comparable with a coordinate gets a
 * marker, the subject gets the red one, and the list beside it selects —
 * click a row, the map flies to that building. Same MapLibre / OpenFreeMap
 * base as the Location panel: free, keyless, light (Edward, 29 Oct 2026).
 *
 * A deal that states only its city has no subject pin — the city centre is not
 * the building — but the map still opens on that city (`area`), not on the
 * whole country, and the city is drawn as a red ring: where the deal is, at the
 * only precision the deal states (Edward, 30 Oct 2026: "the location in red").
 */
import { useEffect, useRef, useState } from 'react'
import * as maplibregl from 'maplibre-gl'
import type { Map as LibreMap, Marker as LibreMarker } from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { LIGHT_MAP_STYLE } from './mapStyle'

// Marker colours read on the light base map: the dark map's pale stone and
// pale yellow all but vanished on it.
const SUBJECT = '#dc2626'
const COMP = '#57534e'
const SELECTED = '#c49a43'
/** The zoom a city-level deal's map opens at: the city, not a block. */
const AREA_ZOOM = 11

export type MapPoint = {
  id: string
  name: string
  lat: number
  lng: number
  detail?: string | null
}

type Props = {
  subject: MapPoint | null
  /** Where to open when there is no subject pin: the deal's stated city. */
  area?: { lat: number; lng: number; label: string } | null
  comps: MapPoint[]
  selectedId: string | null
  onSelect: (id: string | null) => void
  className?: string
}

/** A city, not a building: a hollow red ring, so it cannot be read as a pin on a site. */
function cityRingElement(label: string): HTMLElement {
  const el = document.createElement('div')
  el.title = label
  el.setAttribute('role', 'img')
  el.setAttribute('aria-label', label)
  el.dataset.testid = 'comps-map-city-ring'
  el.style.cssText = [
    'width:46px',
    'height:46px',
    'border-radius:9999px',
    `border:3px solid ${SUBJECT}`,
    'background:rgba(220,38,38,0.14)',
    'box-shadow:0 0 0 1px rgba(255,255,255,0.85)',
    'pointer-events:none',
  ].join(';')
  return el
}

function markerElement(color: string, label: string, emphasised: boolean): HTMLElement {
  const el = document.createElement('button')
  el.type = 'button'
  el.title = label
  el.setAttribute('aria-label', label)
  el.style.cssText = [
    `width:${emphasised ? 16 : 12}px`,
    `height:${emphasised ? 16 : 12}px`,
    'border-radius:9999px',
    `background:${color}`,
    `border:2px solid ${emphasised ? '#0b0b0b' : '#ffffff'}`,
    'box-shadow:0 1px 3px rgba(0,0,0,0.45)',
    'cursor:pointer',
    'padding:0',
  ].join(';')
  return el
}

export function CompsMap({ subject, area = null, comps, selectedId, onSelect, className = '' }: Props) {
  const el = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<LibreMap | null>(null)
  const markersRef = useRef<Map<string, LibreMarker>>(new Map())
  const ringRef = useRef<LibreMarker | null>(null)
  const [error, setError] = useState<string | null>(null)

  const points = [...(subject ? [subject] : []), ...comps]
  const pointsKey = points.map((p) => `${p.id}:${p.lat},${p.lng}`).join('|')

  // One map for the life of the panel.
  useEffect(() => {
    if (!el.current || mapRef.current) return
    try {
      const map = new maplibregl.Map({
        container: el.current,
        style: LIGHT_MAP_STYLE,
        center: subject ? [subject.lng, subject.lat] : area ? [area.lng, area.lat] : [-98.5, 39.8],
        zoom: subject ? 12 : area ? AREA_ZOOM : 3,
        attributionControl: { compact: true },
        keyboard: false,
        dragRotate: false,
        pitchWithRotate: false,
        touchPitch: false,
      })
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')
      map.once('idle', () => {
        el.current
          ?.querySelector('.maplibregl-ctrl-attrib.maplibregl-compact-show')
          ?.classList.remove('maplibregl-compact-show')
      })
      map.on('error', (event) => {
        const message = event?.error?.message
        if (message && /style|tile|fetch|network/i.test(message)) setError('The map tiles could not be loaded.')
      })
      mapRef.current = map
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The map failed to start.')
    }
    return () => {
      markersRef.current.forEach((m) => m.remove())
      markersRef.current.clear()
      ringRef.current?.remove()
      ringRef.current = null
      mapRef.current?.remove()
      mapRef.current = null
    }
    // The subject only seeds the first view; the fit below follows the points.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // A city-only deal's city point often arrives after the map is up: the site
  // lookup that places the city answers later. With no pin or comp to fit, the
  // view goes to the city when it does, rather than staying on the whole country.
  const areaKey = area ? `${area.lat},${area.lng}` : ''
  useEffect(() => {
    const map = mapRef.current
    if (!map || !area || points.length > 0) return
    map.jumpTo({ center: [area.lng, area.lat], zoom: AREA_ZOOM })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaKey, pointsKey])

  // The city ring, for a deal with no building to pin. A subject pin, when
  // there is one, is the location and the ring is not drawn.
  const ringShown = !subject && area != null
  useEffect(() => {
    const map = mapRef.current
    ringRef.current?.remove()
    ringRef.current = null
    if (!map || !ringShown || !area) return
    ringRef.current = new maplibregl.Marker({ element: cityRingElement(`${area.label}: the city, not the site`), anchor: 'center' })
      .setLngLat([area.lng, area.lat])
      .addTo(map)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaKey, ringShown])

  useEffect(() => {
    if (!el.current) return
    const observer = new ResizeObserver(() => mapRef.current?.resize())
    observer.observe(el.current)
    return () => observer.disconnect()
  }, [])

  // Markers follow the points; the view fits them all.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    markersRef.current.forEach((m) => m.remove())
    markersRef.current.clear()
    for (const point of points) {
      const isSubject = subject?.id === point.id
      const marker = new maplibregl.Marker({
        element: markerElement(isSubject ? SUBJECT : point.id === selectedId ? SELECTED : COMP, point.name, isSubject || point.id === selectedId),
        anchor: 'center',
      })
        .setLngLat([point.lng, point.lat])
        .setPopup(
          new maplibregl.Popup({ closeButton: false, offset: 12 }).setHTML(
            // MapLibre's popup is white: the text was pale stone on it, and unreadable.
            `<div style="font:11px/1.3 ui-sans-serif,system-ui;color:#1c1917"><strong>${escapeHtml(point.name)}</strong>${
              point.detail ? `<br><span style="color:#57534e">${escapeHtml(point.detail)}</span>` : ''
            }</div>`,
          ),
        )
        .addTo(map)
      marker.getElement().addEventListener('click', (event) => {
        event.stopPropagation()
        onSelect(isSubject ? null : point.id)
      })
      markersRef.current.set(point.id, marker)
    }
    if (points.length > 0) {
      // The city's centre is in the frame too, so the ring and the comps show together.
      const framed = !subject && area ? [...points, { lat: area.lat, lng: area.lng }] : points
      const bounds = framed.reduce(
        (b, p) => b.extend([p.lng, p.lat]),
        new maplibregl.LngLatBounds([framed[0].lng, framed[0].lat], [framed[0].lng, framed[0].lat]),
      )
      map.fitBounds(bounds, { padding: 40, maxZoom: 14, duration: 0 })
    }
    map.resize()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointsKey])

  // Selection: recolour the marker and go there.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    markersRef.current.forEach((marker, id) => {
      const point = points.find((p) => p.id === id)
      if (!point) return
      const isSubject = subject?.id === id
      const active = id === selectedId
      const element = marker.getElement()
      element.style.background = isSubject ? SUBJECT : active ? SELECTED : COMP
      element.style.width = element.style.height = isSubject || active ? '16px' : '12px'
      const popup = marker.getPopup()
      if (active && !popup?.isOpen()) marker.togglePopup()
      else if (!active && popup?.isOpen()) popup.remove()
    })
    const target = points.find((p) => p.id === selectedId)
    if (target) map.flyTo({ center: [target.lng, target.lat], zoom: Math.max(map.getZoom(), 14), duration: 600 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId])

  return (
    <div className={`relative overflow-hidden rounded-md border border-white/[0.06] bg-black/40 ${className}`}>
      <div ref={el} className="absolute inset-0" aria-label="Comparables map" data-testid="comps-map" />
      {!subject && (
        <p className="pointer-events-none absolute left-2 top-2 rounded bg-black/80 p-2 text-[10px] text-stone-300" data-testid="comps-map-no-subject">
          {area ? `${area.label} · city level: the red ring is the city, not the site.` : 'Subject coordinates are not recorded.'}
        </p>
      )}
      {error ? (
        <div className="absolute inset-0 flex items-center justify-center bg-[#090909] px-6 text-center text-[11px] text-red-300">
          {error}
        </div>
      ) : null}
      {comps.length === 0 ? (
        <div className="pointer-events-none absolute bottom-1.5 left-1.5 rounded bg-black/70 px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-wider text-stone-400">
          {subject ? 'Red = subject · ' : area ? 'Red ring = city · ' : ''}No comparable has a coordinate yet
        </div>
      ) : (
        <div className="pointer-events-none absolute bottom-1.5 left-1.5 rounded bg-black/70 px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-wider text-amber-200">
          {comps.length} comparables on the map · {subject ? 'red = subject' : area ? 'red ring = city' : 'no subject location'}
        </div>
      )}
    </div>
  )
}

function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string)
}

export default CompsMap
